import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { DefaultButton } from 'common/components';
import {
  getTopDownAllocations,
  getTopDownDistributionPlan,
  previewTopDownPolicy,
  updateTopDownDistributionPlan,
  updateTopDownPolicy,
} from '../../api';
import { staticStyles } from './style';

const DEFAULT_SAMPLE_GROSS = '26';
const padDatePart = value => String(value).padStart(2, '0');

const currentMonthDateRange = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = padDatePart(today.getMonth() + 1);
  return {
    start: `${year}-${month}-01`,
    end: `${year}-${month}-${padDatePart(today.getDate())}`,
  };
};

const firstErrorMessage = value => {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(firstErrorMessage).find(Boolean);
  }
  if (value && typeof value === 'object') {
    return Object.values(value).map(firstErrorMessage).find(Boolean);
  }
  return '';
};

const getErrorMessage = error => {
  const data = error && error.response && error.response.data;
  if (data && data.detail) {
    return firstErrorMessage(data.detail) || String(data.detail);
  }
  if (data && typeof data === 'object') {
    const firstMessage = firstErrorMessage(data);
    if (firstMessage) {
      return firstMessage;
    }
  }
  if (error && error.message) {
    return error.message;
  }
  return 'Unable to load the top-down distribution settings.';
};

const successfulResponseData = response => {
  if (!response || typeof response.status !== 'number' || response.status < 200 || response.status >= 300) {
    const error = new Error('Top-down distribution request failed.');
    error.response = response;
    throw error;
  }
  return response.data;
};

const toNumber = value => {
  if (value && typeof value === 'object' && value.amount !== undefined) {
    return Number(value.amount);
  }
  return Number(value);
};

const formatPercentage = value => {
  const percentage = Number(value);
  if (!Number.isFinite(percentage)) {
    return '0';
  }
  return percentage.toFixed(6).replace(/\.?0+$/, '');
};

const formatSignedUsd = value => {
  const amount = toNumber(value);
  if (!Number.isFinite(amount)) {
    return '—';
  }
  const sign = amount >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(amount).toFixed(2)}`;
};

const formatControlMode = value => {
  if (value === 'master') {
    return 'Master controlled';
  }
  return 'Brokerage controlled';
};

const normaliseDepthRules = policy =>
  ((policy && policy.depthRules) || []).map(rule => ({
    level: Number(rule.level),
    percentage: String(rule.percentage),
  }));

const overrideAccountId = override => Number(override.partnershipAccountId);

const overrideName = override => override.name || override.partnerCode || `Account #${overrideAccountId(override)}`;

const overrideOptionLabel = override => {
  const name = overrideName(override);
  return override.partnerCode && override.partnerCode !== name ? `${name} (${override.partnerCode})` : name;
};

const normaliseOverrides = policy =>
  ((policy && policy.overrides) || []).map(override => ({
    partnershipAccountId: overrideAccountId(override),
    level: override.level ? Number(override.level) : null,
    name: overrideName(override),
    partnerCode: override.partnerCode || '',
    path: override.path || [],
    percentage: String(override.percentage),
  }));

const getOverrideOptions = plan => {
  const source = plan.overrideOptions || [];
  return source.map(option => ({
    partnershipAccountId: overrideAccountId(option),
    level: option.level ? Number(option.level) : null,
    name: overrideName(option),
    partnerCode: option.partnerCode || '',
    path: option.path || [],
  }));
};

const ALLOCATION_PAGE_SIZE = 50;

const nextPageNumber = next => {
  if (!next) {
    return null;
  }
  if (typeof next === 'number' || /^\d+$/.test(String(next))) {
    return Number(next);
  }
  const match = String(next).match(/[?&]page=(\d+)/);
  return match ? Number(match[1]) : null;
};

const formatAllocationDate = allocation => allocation.rewardDate || '—';

const TopDownDistributionPanel = ({
  accountId,
  allowCreate,
  auditView,
  initialProgramId,
  onPlanChange,
  programOptions,
  showWhenEmpty,
}) => {
  const depthRuleKeySequence = useRef(0);
  const planRequestSequence = useRef(0);
  const allocationsRequestSequence = useRef(0);
  const initialAllocationDateRange = useRef(currentMonthDateRange()).current;
  const mounted = useRef(true);
  const currentAccountId = useRef(accountId);
  currentAccountId.current = accountId;
  const [plan, setPlan] = useState(null);
  const [depthRules, setDepthRules] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [sampleGross, setSampleGross] = useState(DEFAULT_SAMPLE_GROSS);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [managing, setManaging] = useState(false);
  const [managementControlMode, setManagementControlMode] = useState('broker');
  const [creationControlMode, setCreationControlMode] = useState('broker');
  const [creationProgramId, setCreationProgramId] = useState(
    initialProgramId || (programOptions[0] && programOptions[0].id) || ''
  );
  const [missing, setMissing] = useState(false);
  const [allocations, setAllocations] = useState([]);
  const [allocationCount, setAllocationCount] = useState(0);
  const [allocationNextPage, setAllocationNextPage] = useState(null);
  const [allocationStart, setAllocationStart] = useState(initialAllocationDateRange.start);
  const [allocationEnd, setAllocationEnd] = useState(initialAllocationDateRange.end);
  const [allocationDateRange, setAllocationDateRange] = useState(initialAllocationDateRange);
  const [allocationsLoading, setAllocationsLoading] = useState(true);
  const [allocationsLoadingMore, setAllocationsLoadingMore] = useState(false);
  const [allocationsError, setAllocationsError] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setPreviewLoading(false);
    setSaving(false);
    setManaging(false);
  }, [accountId]);

  useEffect(() => {
    setCreationProgramId(initialProgramId || (programOptions[0] && programOptions[0].id) || '');
  }, [initialProgramId, programOptions]);

  const requestMatchesCurrentAccount = useCallback(
    requestAccountId => mounted.current && String(currentAccountId.current) === String(requestAccountId),
    []
  );

  const withDepthRuleKey = useCallback(rule => {
    depthRuleKeySequence.current += 1;
    return {
      ...rule,
      rowKey: depthRuleKeySequence.current,
    };
  }, []);

  const applyPlan = useCallback(
    data => {
      if (!data) {
        setPlan(null);
        setMissing(true);
        return;
      }

      const optionsById = getOverrideOptions(data).reduce(
        (result, option) => ({
          ...result,
          [option.partnershipAccountId]: option,
        }),
        {}
      );

      setPlan(data);
      setManagementControlMode(data.controlMode || 'broker');
      setPreview(null);
      setDepthRules(normaliseDepthRules(data).map(withDepthRuleKey));
      setOverrides(
        normaliseOverrides(data).map(override => ({
          ...override,
          ...(optionsById[override.partnershipAccountId] || {}),
          percentage: override.percentage,
        }))
      );
      setMissing(false);
    },
    [withDepthRuleKey]
  );

  const loadPlan = useCallback(async () => {
    planRequestSequence.current += 1;
    const requestSequence = planRequestSequence.current;
    setPlan(null);
    setDepthRules([]);
    setOverrides([]);
    setPreview(null);
    setMissing(false);
    setNotice('');
    if (!accountId) {
      setLoading(false);
      setMissing(true);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await getTopDownDistributionPlan(accountId);
      if (requestSequence !== planRequestSequence.current) {
        return;
      }
      applyPlan(successfulResponseData(response));
    } catch (requestError) {
      if (requestSequence !== planRequestSequence.current) {
        return;
      }
      if (requestError.response && (requestError.response.status === 403 || requestError.response.status === 404)) {
        setMissing(true);
      } else {
        setError(getErrorMessage(requestError));
      }
    } finally {
      if (requestSequence === planRequestSequence.current) {
        setLoading(false);
      }
    }
  }, [accountId, applyPlan]);

  useEffect(() => {
    loadPlan();
    return () => {
      planRequestSequence.current += 1;
    };
  }, [loadPlan]);

  const loadAllocations = useCallback(
    async (page = 1, append = false) => {
      allocationsRequestSequence.current += 1;
      const requestSequence = allocationsRequestSequence.current;
      if (!accountId) {
        setAllocationsLoading(false);
        return;
      }

      if (append) {
        setAllocationsLoadingMore(true);
      } else {
        setAllocationsLoading(true);
        setAllocationsError('');
        setAllocations([]);
        setAllocationCount(0);
        setAllocationNextPage(null);
      }
      try {
        const response = await getTopDownAllocations(accountId, {
          page,
          page_size: ALLOCATION_PAGE_SIZE,
          start: allocationDateRange.start,
          end: allocationDateRange.end,
        });
        const allocationPage = successfulResponseData(response);
        if (requestSequence !== allocationsRequestSequence.current) {
          return;
        }
        setAllocations(current => (append ? [...current, ...allocationPage.results] : allocationPage.results));
        setAllocationCount(allocationPage.count);
        setAllocationNextPage(nextPageNumber(allocationPage.next));
      } catch (requestError) {
        if (requestSequence !== allocationsRequestSequence.current) {
          return;
        }
        if (!requestError.response || (requestError.response.status !== 403 && requestError.response.status !== 404)) {
          setAllocationsError(getErrorMessage(requestError));
        }
      } finally {
        if (requestSequence === allocationsRequestSequence.current) {
          setAllocationsLoading(false);
          setAllocationsLoadingMore(false);
        }
      }
    },
    [accountId, allocationDateRange]
  );

  useEffect(() => {
    loadAllocations();
    return () => {
      allocationsRequestSequence.current += 1;
    };
  }, [loadAllocations]);

  const overrideOptions = useMemo(() => {
    if (!plan) {
      return [];
    }
    const optionsById = {};
    [...getOverrideOptions(plan), ...overrides].forEach(option => {
      optionsById[option.partnershipAccountId] = option;
    });
    return Object.values(optionsById);
  }, [overrides, plan]);

  const retainedPercentage = useMemo(() => {
    if (preview && preview.masterPercentage !== undefined) {
      return formatPercentage(preview.masterPercentage);
    }
    const total = depthRules.reduce((sum, rule) => sum + (Number(rule.percentage) || 0), 0);
    return formatPercentage(Math.max(0, 100 - total));
  }, [depthRules, preview]);

  const policyPayload = useCallback(
    () => ({
      depthRules: depthRules.map(rule => ({
        level: Number(rule.level),
        percentage: String(rule.percentage),
      })),
      overrides: overrides.map(override => ({
        partnershipAccountId: Number(override.partnershipAccountId),
        percentage: String(override.percentage),
      })),
    }),
    [depthRules, overrides]
  );

  const applyAllocationDateRange = () => {
    if (!allocationStart || !allocationEnd || allocationStart > allocationEnd) {
      setAllocationsError('Choose a valid allocation date range.');
      return;
    }
    setAllocationsError('');
    setAllocationDateRange({ start: allocationStart, end: allocationEnd });
  };

  const validatePolicy = () => {
    const levels = depthRules.map(rule => Number(rule.level));
    const percentages = depthRules.map(rule => Number(rule.percentage));
    if (
      levels.some(level => !Number.isInteger(level) || level < 2) ||
      depthRules.some(rule => rule.percentage === '') ||
      percentages.some(percentage => !Number.isFinite(percentage) || percentage < 0 || percentage > 100)
    ) {
      return 'Every depth rule needs a level of 2 or greater and a percentage between 0 and 100.';
    }
    if (new Set(levels).size !== levels.length) {
      return 'Each depth level can only be configured once.';
    }
    if (percentages.reduce((total, percentage) => total + percentage, 0) > 100) {
      return 'Depth percentages cannot distribute more than 100%.';
    }
    if (
      overrides.some(
        override =>
          !override.partnershipAccountId ||
          override.percentage === '' ||
          !Number.isFinite(Number(override.percentage)) ||
          Number(override.percentage) < 0 ||
          Number(override.percentage) > 100
      )
    ) {
      return 'Every named override needs a percentage between 0 and 100.';
    }
    return '';
  };

  const updateDepthRule = (index, field, value) => {
    setDepthRules(current =>
      current.map((rule, ruleIndex) => (ruleIndex === index ? { ...rule, [field]: value } : rule))
    );
    setPreview(null);
    setNotice('');
  };

  const addDepthRule = () => {
    const nextLevel = Math.max(1, ...depthRules.map(rule => Number(rule.level) || 1)) + 1;
    setDepthRules(current => [...current, withDepthRuleKey({ level: nextLevel, percentage: '0' })]);
    setPreview(null);
    setNotice('');
  };

  const removeDepthRule = index => {
    setDepthRules(current => current.filter((rule, ruleIndex) => ruleIndex !== index));
    setPreview(null);
    setNotice('');
  };

  const updateOverride = (index, value) => {
    setOverrides(current =>
      current.map((override, overrideIndex) =>
        overrideIndex === index ? { ...override, percentage: value } : override
      )
    );
    setPreview(null);
    setNotice('');
  };

  const removeOverride = index => {
    setOverrides(current => current.filter((override, overrideIndex) => overrideIndex !== index));
    setPreview(null);
    setNotice('');
  };

  const addOverride = event => {
    const partnershipAccountId = Number(event.target.value);
    const option = overrideOptions.find(item => item.partnershipAccountId === partnershipAccountId);
    if (!option || overrides.some(item => item.partnershipAccountId === partnershipAccountId)) {
      return;
    }
    setOverrides(current => [...current, { ...option, percentage: '0' }]);
    setPreview(null);
    setNotice('');
    event.target.value = '';
  };

  const handlePreview = async () => {
    const sampleAmount = Number(sampleGross);
    if (sampleGross === '' || !Number.isFinite(sampleAmount)) {
      setError('Enter a valid signed sample gross amount.');
      return;
    }
    const validationError = validatePolicy();
    if (validationError) {
      setError(validationError);
      return;
    }

    setPreviewLoading(true);
    setError('');
    setNotice('');
    const requestAccountId = accountId;
    try {
      const response = await previewTopDownPolicy(accountId, {
        ...policyPayload(),
        sampleGross: String(sampleGross),
      });
      if (!requestMatchesCurrentAccount(requestAccountId)) {
        return;
      }
      setPreview(successfulResponseData(response));
    } catch (requestError) {
      if (requestMatchesCurrentAccount(requestAccountId)) {
        setError(getErrorMessage(requestError));
      }
    } finally {
      if (requestMatchesCurrentAccount(requestAccountId)) {
        setPreviewLoading(false);
      }
    }
  };

  const handleSavePolicy = async () => {
    const validationError = validatePolicy();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    setNotice('');
    const requestAccountId = accountId;
    try {
      const response = await updateTopDownPolicy(accountId, policyPayload());
      const updatedPlan = successfulResponseData(response);
      if (requestMatchesCurrentAccount(requestAccountId)) {
        applyPlan(updatedPlan);
        setNotice('The distribution policy has been saved.');
      }
      onPlanChange(updatedPlan);
    } catch (requestError) {
      if (requestMatchesCurrentAccount(requestAccountId)) {
        setError(getErrorMessage(requestError));
      }
    } finally {
      if (requestMatchesCurrentAccount(requestAccountId)) {
        setSaving(false);
      }
    }
  };

  const handleCreatePlan = async () => {
    const programId = Number(creationProgramId);
    if (!programId) {
      setError('Choose a top-down reward program.');
      return;
    }

    setManaging(true);
    setError('');
    setNotice('');
    const requestAccountId = accountId;
    try {
      const response = await updateTopDownDistributionPlan(accountId, {
        programId,
        controlMode: creationControlMode,
      });
      const createdPlan = successfulResponseData(response);
      if (requestMatchesCurrentAccount(requestAccountId)) {
        applyPlan(createdPlan);
        setNotice('The active Master plan has been created.');
      }
      onPlanChange(createdPlan);
    } catch (requestError) {
      if (requestMatchesCurrentAccount(requestAccountId)) {
        setError(getErrorMessage(requestError));
      }
    } finally {
      if (requestMatchesCurrentAccount(requestAccountId)) {
        setManaging(false);
      }
    }
  };

  const handlePlanUpdate = async updates => {
    setManaging(true);
    setError('');
    setNotice('');
    const requestAccountId = accountId;
    try {
      const response = await updateTopDownDistributionPlan(accountId, updates);
      const updatedPlan = successfulResponseData(response);
      if (requestMatchesCurrentAccount(requestAccountId)) {
        applyPlan(updatedPlan);
        setNotice('The Master plan has been updated.');
      }
      onPlanChange(updatedPlan);
    } catch (requestError) {
      if (requestMatchesCurrentAccount(requestAccountId)) {
        setError(getErrorMessage(requestError));
      }
    } finally {
      if (requestMatchesCurrentAccount(requestAccountId)) {
        setManaging(false);
      }
    }
  };

  const policyBusy = previewLoading || saving || managing;

  if (loading && allocationsLoading) {
    return <div className="TopDownDistributionPanel TopDownDistributionPanel--loading">Loading distribution…</div>;
  }

  if (!plan && missing && !allowCreate && !allocations.length && !showWhenEmpty && !allocationsError) {
    return null;
  }

  if (!plan && error && (!allowCreate || !missing)) {
    return (
      <div className="TopDownDistributionPanel TopDownDistributionPanel--error" role="alert">
        {error}
      </div>
    );
  }

  const canEdit = Boolean(plan && plan.canEdit);
  const canManagePlan = Boolean(plan && plan.canManagePlan);
  const hasNegativeAllocations = allocations.some(allocation => toNumber(allocation.amount) < 0);
  let allocationVisibility = 'Your received rewards';
  if (auditView || canManagePlan) {
    allocationVisibility = 'Brokerage audit view';
  } else if (plan) {
    allocationVisibility = 'Complete Master tree';
  }

  return (
    <section className="TopDownDistributionPanel" aria-label="Top-down reward distribution">
      {plan ? (
        <div className="TopDownDistributionPanel__header">
          <div>
            <h3>Top-down reward distribution</h3>
            <p>{plan.programTitle || 'Master IB distribution program'}</p>
          </div>
        </div>
      ) : (
        <div className="TopDownDistributionPanel__header">
          <div>
            <h3>Top-down reward allocations</h3>
            <p>Rewards received from a Master IB distribution plan.</p>
          </div>
        </div>
      )}

      {!plan && missing && allowCreate && (
        <div className="TopDownDistributionPanel__create">
          <div className="TopDownDistributionPanel__section-header">
            <div>
              <h4>Create top-down Master plan</h4>
              <p>This partnership account will be the explicit root of a new distribution tree.</p>
            </div>
          </div>
          {programOptions.length ? (
            <div className="TopDownDistributionPanel__create-controls">
              <label htmlFor={`top-down-create-program-${accountId}`}>Gross reward program</label>
              <select
                id={`top-down-create-program-${accountId}`}
                disabled={policyBusy}
                value={creationProgramId}
                onChange={event => setCreationProgramId(event.target.value)}>
                {programOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.title || option.label}
                  </option>
                ))}
              </select>
              <label htmlFor={`top-down-create-control-${accountId}`}>Policy control</label>
              <select
                id={`top-down-create-control-${accountId}`}
                disabled={policyBusy}
                value={creationControlMode}
                onChange={event => setCreationControlMode(event.target.value)}>
                <option value="broker">Brokerage controlled</option>
                <option value="master">Master controlled</option>
              </select>
              <DefaultButton
                textId="Create Master plan"
                noTranslate
                filled
                fitContent
                loading={managing}
                disabled={policyBusy || !creationProgramId}
                onClick={handleCreatePlan}
              />
            </div>
          ) : (
            <p className="TopDownDistributionPanel__message TopDownDistributionPanel__message--error" role="alert">
              No top-down reward program is available for this brokerage.
            </p>
          )}
          {error && (
            <p className="TopDownDistributionPanel__message TopDownDistributionPanel__message--error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}

      {plan && (
        <dl className="TopDownDistributionPanel__summary">
          <div>
            <dt>Control</dt>
            <dd>{formatControlMode(plan.controlMode)}</dd>
          </div>
          <div>
            <dt>Master retained</dt>
            <dd>{retainedPercentage}% by default</dd>
          </div>
        </dl>
      )}

      {plan && (
        <>
          {canManagePlan && (
            <div className="TopDownDistributionPanel__management">
              <div>
                <strong>Brokerage plan controls</strong>
                <p>Choose who edits the allocation policy for this tree.</p>
              </div>
              <div className="TopDownDistributionPanel__management-controls">
                <label htmlFor={`top-down-control-mode-${accountId}`}>Policy control</label>
                <select
                  id={`top-down-control-mode-${accountId}`}
                  disabled={policyBusy}
                  value={managementControlMode}
                  onChange={event => {
                    setManagementControlMode(event.target.value);
                    setError('');
                    setNotice('');
                  }}>
                  <option value="broker">Brokerage controlled</option>
                  <option value="master">Master controlled</option>
                </select>
                <DefaultButton
                  textId="Save control mode"
                  noTranslate
                  fitContent
                  loading={managing}
                  disabled={policyBusy || managementControlMode === plan.controlMode}
                  onClick={() => handlePlanUpdate({ controlMode: managementControlMode })}
                />
              </div>
            </div>
          )}

          <div className="TopDownDistributionPanel__rules">
            <div className="TopDownDistributionPanel__section-header">
              <h4>Depth rules</h4>
              {canEdit && (
                <DefaultButton
                  className="TopDownDistributionPanel__small-button"
                  textId="Add level"
                  noTranslate
                  fitContent
                  disabled={policyBusy}
                  onClick={addDepthRule}
                />
              )}
            </div>
            {depthRules.length ? (
              <div className="TopDownDistributionPanel__rule-list">
                {depthRules.map((rule, index) => (
                  <div className="TopDownDistributionPanel__rule" key={rule.rowKey}>
                    <label htmlFor={`top-down-level-${accountId}-${index}`}>Level</label>
                    {canEdit ? (
                      <input
                        id={`top-down-level-${accountId}-${index}`}
                        min="2"
                        step="1"
                        type="number"
                        disabled={policyBusy}
                        value={rule.level}
                        onChange={event => updateDepthRule(index, 'level', event.target.value)}
                      />
                    ) : (
                      <span>{rule.level}</span>
                    )}
                    <label htmlFor={`top-down-percentage-${accountId}-${index}`}>Reward share</label>
                    {canEdit ? (
                      <div className="TopDownDistributionPanel__percentage-input">
                        <input
                          id={`top-down-percentage-${accountId}-${index}`}
                          max="100"
                          min="0"
                          step="0.000001"
                          type="number"
                          disabled={policyBusy}
                          value={rule.percentage}
                          onChange={event => updateDepthRule(index, 'percentage', event.target.value)}
                        />
                        <span>%</span>
                      </div>
                    ) : (
                      <span>{formatPercentage(rule.percentage)}%</span>
                    )}
                    {canEdit && (
                      <button
                        aria-label={`Remove level ${rule.level}`}
                        className="TopDownDistributionPanel__remove"
                        disabled={policyBusy}
                        type="button"
                        onClick={() => removeDepthRule(index)}>
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="TopDownDistributionPanel__empty">No descendant depth rules. The Master retains 100%.</p>
            )}
          </div>

          <div className="TopDownDistributionPanel__overrides">
            <div className="TopDownDistributionPanel__section-header">
              <h4>Named overrides</h4>
              {canEdit && overrideOptions.length > overrides.length && (
                <select aria-label="Add named override" defaultValue="" disabled={policyBusy} onChange={addOverride}>
                  <option disabled value="">
                    Add partner…
                  </option>
                  {overrideOptions
                    .filter(
                      option => !overrides.some(item => item.partnershipAccountId === option.partnershipAccountId)
                    )
                    .map(option => (
                      <option key={option.partnershipAccountId} value={option.partnershipAccountId}>
                        {overrideOptionLabel(option)}
                      </option>
                    ))}
                </select>
              )}
            </div>
            {overrides.length ? (
              <div className="TopDownDistributionPanel__override-list">
                {overrides.map((override, index) => (
                  <div className="TopDownDistributionPanel__override" key={override.partnershipAccountId}>
                    <div className="TopDownDistributionPanel__override-details">
                      <strong>{override.name}</strong>
                      {override.partnerCode && <small>{override.partnerCode}</small>}
                      {override.path.length > 1 && (
                        <div className="TopDownDistributionPanel__override-path">
                          {override.path.slice(1).map(account => (
                            <small key={account.partnershipAccountId}>↑ {overrideOptionLabel(account)}</small>
                          ))}
                        </div>
                      )}
                      {override.level && <small>Level {override.level}</small>}
                    </div>
                    {canEdit ? (
                      <div className="TopDownDistributionPanel__percentage-input">
                        <input
                          aria-label={`${override.name} reward share`}
                          max="100"
                          min="0"
                          step="0.000001"
                          type="number"
                          disabled={policyBusy}
                          value={override.percentage}
                          onChange={event => updateOverride(index, event.target.value)}
                        />
                        <span>%</span>
                      </div>
                    ) : (
                      <span>{formatPercentage(override.percentage)}%</span>
                    )}
                    {canEdit && (
                      <button
                        aria-label={`Remove ${override.name} override`}
                        className="TopDownDistributionPanel__remove"
                        disabled={policyBusy}
                        type="button"
                        onClick={() => removeOverride(index)}>
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="TopDownDistributionPanel__empty">No partner-specific overrides.</p>
            )}
          </div>

          <div className="TopDownDistributionPanel__preview">
            <div className="TopDownDistributionPanel__section-header">
              <div>
                <h4>Sample gross preview</h4>
                <p>Use a positive or negative gross amount to verify the signed allocation.</p>
              </div>
            </div>
            <div className="TopDownDistributionPanel__preview-controls">
              <label htmlFor={`top-down-sample-${accountId}`}>Sample gross, USD</label>
              <input
                id={`top-down-sample-${accountId}`}
                step="0.01"
                type="number"
                disabled={policyBusy}
                value={sampleGross}
                onChange={event => {
                  setSampleGross(event.target.value);
                  setPreview(null);
                }}
              />
              <DefaultButton
                textId="Preview"
                noTranslate
                filled
                fitContent
                loading={previewLoading}
                disabled={policyBusy}
                onClick={handlePreview}
              />
            </div>
            {preview && (
              <div className="TopDownDistributionPanel__preview-result">
                <div className="TopDownDistributionPanel__preview-total">
                  <span>Gross pool</span>
                  <strong>
                    {formatSignedUsd(preview.grossAmount !== undefined ? preview.grossAmount : sampleGross)}
                  </strong>
                </div>
                {(preview.allocations || []).map(allocation => (
                  <div key={`${allocation.partnershipAccountId || allocation.level}-${allocation.percentage}`}>
                    <span>{allocation.name || `Level ${allocation.level}`}</span>
                    <span>{formatPercentage(allocation.percentage)}%</span>
                    <strong>{formatSignedUsd(allocation.amount)}</strong>
                  </div>
                ))}
                <div className="TopDownDistributionPanel__preview-master">
                  <span>Master retained</span>
                  <span>{retainedPercentage}%</span>
                  <strong>{formatSignedUsd(preview.masterAmount)}</strong>
                </div>
                {(preview.pathPreviews || []).map(pathPreview => (
                  <div className="TopDownDistributionPanel__path-preview" key={pathPreview.partnershipAccountId}>
                    <strong>Path to {pathPreview.partnerCode}</strong>
                    {(pathPreview.allocations || []).map(allocation => (
                      <span key={allocation.partnershipAccountId}>
                        {allocation.partnerCode}: {formatPercentage(allocation.percentage)}% (
                        {formatSignedUsd(allocation.amount)})
                      </span>
                    ))}
                    <span>
                      Master: {formatPercentage(pathPreview.masterPercentage)}% (
                      {formatSignedUsd(pathPreview.masterAmount)})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p className="TopDownDistributionPanel__message TopDownDistributionPanel__message--error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="TopDownDistributionPanel__message TopDownDistributionPanel__message--success">{notice}</p>
          )}

          {canEdit && (
            <div className="TopDownDistributionPanel__save-policy">
              <DefaultButton
                textId="Save policy"
                noTranslate
                filled
                fitContent
                loading={saving}
                disabled={policyBusy}
                onClick={handleSavePolicy}
              />
            </div>
          )}
        </>
      )}

      <div className="TopDownDistributionPanel__allocations">
        <div className="TopDownDistributionPanel__section-header">
          <div>
            <h4>Reward allocations</h4>
            <p>
              {allocationVisibility}
              {allocationCount ? ` · Showing ${allocations.length} of ${allocationCount} daily partner totals` : ''}
            </p>
          </div>
        </div>

        <div className="TopDownDistributionPanel__allocation-filters">
          <label htmlFor={`top-down-allocation-start-${accountId}`}>From</label>
          <input
            id={`top-down-allocation-start-${accountId}`}
            type="date"
            value={allocationStart}
            onChange={event => setAllocationStart(event.target.value)}
          />
          <label htmlFor={`top-down-allocation-end-${accountId}`}>To</label>
          <input
            id={`top-down-allocation-end-${accountId}`}
            type="date"
            value={allocationEnd}
            onChange={event => setAllocationEnd(event.target.value)}
          />
          <DefaultButton
            textId="Apply"
            noTranslate
            fitContent
            loading={allocationsLoading}
            disabled={allocationsLoading || allocationsLoadingMore}
            onClick={applyAllocationDateRange}
          />
        </div>

        {hasNegativeAllocations && (
          <p className="TopDownDistributionPanel__allocation-warning">
            Negative totals are adjustments that reduce the receiving partner&apos;s balance.
          </p>
        )}
        {allocationsError && (
          <p className="TopDownDistributionPanel__message TopDownDistributionPanel__message--error" role="alert">
            {allocationsError}
          </p>
        )}
        {allocationsLoading && <p className="TopDownDistributionPanel__empty">Loading reward allocations…</p>}
        {!allocationsLoading && Boolean(allocations.length) && (
          <>
            <div className="TopDownDistributionPanel__allocation-table-wrap">
              <table className="TopDownDistributionPanel__allocation-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Receiving partner</th>
                    <th>Trades</th>
                    <th>Total allocation</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map(allocation => (
                    <tr
                      className={
                        toNumber(allocation.amount) < 0 ? 'TopDownDistributionPanel__allocation-row--negative' : ''
                      }
                      key={`${allocation.rewardDate}-${allocation.beneficiaryId}`}>
                      <td>{formatAllocationDate(allocation)}</td>
                      <td>{allocation.beneficiaryPartnerCode || '—'}</td>
                      <td>{allocation.tradeCount || 0}</td>
                      <td>
                        <strong>{formatSignedUsd(allocation.amount)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {allocationNextPage && (
              <div className="TopDownDistributionPanel__load-more">
                <DefaultButton
                  textId="Load more"
                  noTranslate
                  fitContent
                  loading={allocationsLoadingMore}
                  disabled={allocationsLoadingMore}
                  onClick={() => loadAllocations(allocationNextPage, true)}
                />
              </div>
            )}
          </>
        )}
        {!allocationsLoading && !allocations.length && (
          <p className="TopDownDistributionPanel__empty">No top-down reward allocations for this date range.</p>
        )}
      </div>

      <style jsx>{staticStyles}</style>
    </section>
  );
};

TopDownDistributionPanel.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  allowCreate: PropTypes.bool,
  auditView: PropTypes.bool,
  initialProgramId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onPlanChange: PropTypes.func,
  programOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      label: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  showWhenEmpty: PropTypes.bool,
};

TopDownDistributionPanel.defaultProps = {
  allowCreate: false,
  auditView: false,
  initialProgramId: null,
  onPlanChange: () => {},
  programOptions: [],
  showWhenEmpty: false,
};

export default TopDownDistributionPanel;
export {
  TopDownDistributionPanel,
  currentMonthDateRange,
  formatPercentage,
  formatSignedUsd,
  normaliseDepthRules,
  normaliseOverrides,
};
